import { useOrchidPro } from "components/Dashboard/DoctorDashboard/contexts/orchid-pro-context";
import { Link } from "react-router-dom";
import ErrorMessage from "components/error-message";
import ToggleSwitch from "components/toggle-switch";
import { ReactComponent as OutlineCopy24 } from "assets/svg/outline-copy-24.svg";
import SuccessMessage from "components/success-message";
import SuccessTag from "components/success-tag/SuccessTag";
import { useTimeOnPageContext } from "contexts/time-on-page";
import React, { useRef, useEffect, useMemo, useState } from "react";
import { Button, Form, Spinner, Tab, Tabs } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import postOrchidPro from "services/postOrchidPro";
import { AmplitudePageName, track } from "utils/amplitude";
import styles from "./Directory.module.scss";

const AMPLITUDE_PAGE_NAME: AmplitudePageName = "pro-settings-directory";

const DIRECTORY_SETTING_OPTIONS = [{ value: "directory", label: "Directory" }];

const DirectorySetting = () => {
  const { setPage } = useTimeOnPageContext();
  useEffect(() => setPage(AMPLITUDE_PAGE_NAME), [setPage]);

  const copyLinkTarget = useRef(null);
  const [copied, setCopied] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const { activeTab } = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return {
      activeTab: query.get("tab") || "",
    };
  }, [location.search]);

  const [tab, setTab] = useState(activeTab || "directory");
  const [changed, setChanged] = useState(false);

  const { orchidPro, setOrchidPro } = useOrchidPro();

  useEffect(() => {
    if (!orchidPro) return;
    setActivate(!orchidPro.unlisted);
  }, [orchidPro]);

  const [activate, setActivate] = useState<boolean>(false);
  const [error, setError] = useState<any>();
  const [success, setSuccess] = useState<string>();

  const [busy, setBusy] = useState(false);

  const saveDirectorySettings = async () => {
    setBusy(true);
    try {
      setOrchidPro(
        await postOrchidPro({
          unlisted: !activate,
        }),
      );
      setSuccess("Saved");
    } catch (error) {
      setError(error);
    } finally {
      setBusy(false);
    }
  };

  const onActivateDirectoryChange = () => {
    setActivate((s) => !s);
    setChanged(true);
  };

  const copyProfileLink = async () => {
    try {
      await navigator.clipboard.writeText(
        `${window.location.origin}/professional-search/${orchidPro?.custom_url}`,
      );
      setCopied(true);
    } catch (error) {
      console.error("Failed to copy profile link", error);
    }
  };

  return (
    <div className={styles["directory"]}>
      {copied && (
        <SuccessTag
          target={copyLinkTarget.current}
          show={copied}
          onHide={() => setCopied(false)}
          placement={"top-end"}
          successMsg={"Copied"}
          duration={1000}
        />
      )}
      {error && (
        <ErrorMessage
          show={true}
          onHide={() => setError(undefined)}
          errorMsg={error?.toString()}
        />
      )}
      {success && (
        <SuccessMessage
          show={true}
          onHide={() => setSuccess(undefined)}
          message={"Saved"}
          duration={1000}
        />
      )}
      <div className={styles["directory__header"]}>
        <h4 className={"rg-32 deep-blue questrial mb-0"}>Directory Setting</h4>
        <Tabs
          activeKey={tab}
          onSelect={(k) => {
            setTab((s) => k || s);
            navigate(`/pro-dashboard/settings/directory?tab=${k}`, { replace: true });
          }}
          className={styles["directory__tabs"]}
        >
          {DIRECTORY_SETTING_OPTIONS.map((el, i) => (
            <Tab
              key={i}
              eventKey={el.value}
              title={el.label}
              tabClassName={styles["directory__tab"]}
              onEnter={() =>
                track(AMPLITUDE_PAGE_NAME, "directory-tab", "click", {
                  eventProperties: { tab: el.value },
                })
              }
            ></Tab>
          ))}
        </Tabs>
      </div>

      <div className={styles["directory__body"]}>
        {tab === "directory" && (
          <>
            <div className={styles["directory__section"]}>
              <div className={styles["directory__section-title"]}>
                Activate Your Profile in the Directory
              </div>
              <div className={"rg-14 mb-4"}>
                By activating, clients will be able to find you through the{" "}
                <Link to="/professional-search">Find My Professional</Link>{" "}
                page.
              </div>

              <div className={styles["directory__settings-input"]}>
                <ToggleSwitch
                  checked={activate}
                  onChange={onActivateDirectoryChange}
                  onClick={() =>
                    track(AMPLITUDE_PAGE_NAME, "autopay-toggle", "click")
                  }
                />
                <span aria-hidden="true">Activate Directory</span>
              </div>

              <hr className={styles["directory__hr"]} />
              <div className={"rg-12 gray01 mb-1"}>Link to your profile</div>
              <div className={"d-flex align-items-center"}>
                <Form.Control
                  type="text"
                  value={`${window.location.origin}/professional-search/${orchidPro?.custom_url}`}
                  readOnly
                  className={styles["directory__link"]}
                />
                <Button
                  ref={copyLinkTarget}
                  variant="link"
                  onClick={() => {
                    copyProfileLink();
                    track(AMPLITUDE_PAGE_NAME, "copy-link", "click");
                  }}
                >
                  <OutlineCopy24 />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      <div className={styles["directory__footer"]}>
        <div className={"gray01 rg-12"}>
          ©2026 Orchid Exchange Inc. All rights reserved
        </div>
        <Button
          variant="primary"
          onClick={() => {
            track(AMPLITUDE_PAGE_NAME, "save-button", "click");
            saveDirectorySettings();
          }}
          className={styles["data-analysis__save-button"]}
          disabled={!changed || busy}
        >
          Save{" "}
          {busy && (
            <Spinner
              as="span"
              animation="grow"
              size="sm"
              role="status"
              aria-hidden="true"
              className="ml-3"
            />
          )}
        </Button>
      </div>
    </div>
  );
};

export default DirectorySetting;
